import { useState, useEffect, useLayoutEffect, useRef, useCallback, useMemo } from 'react';
import '../styles/TimeBilling.css';
import { formatDate, formatValue, Subheader, SubtextTitle } from './FieldComponents';
import { Plus } from 'lucide-react';
import { CreatePayment } from './CreatePayment';
import Modal from './Modal';
import { BillingSettings } from './BillingSettings';
import { BillingEntries } from './BillingEntries';
import {
    billingEntryHasInvoiceLink,
    billingEntryIsEligibleForInvoiceSelection,
    CreateInvoice,
    getBillingEntryTotal,
    getBillingTimeEntryAuthoritativeTotal,
    getInvoiceStatusLabel,
} from './CreateInvoice';
import Alert from './Alert';

export const formatInvoiceLocalDate = (date) => {
    const value = date instanceof Date ? date : new Date(date);
    if (Number.isNaN(value.getTime())) return '';

    const year = value.getFullYear();
    const month = String(value.getMonth() + 1).padStart(2, '0');
    const day = String(value.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

export const createFreshInvoiceData = (now = new Date()) => {
    const invoiceDate = new Date(now);
    const dueDate = new Date(now);
    dueDate.setDate(dueDate.getDate() + 30);

    return {
        invoice: {
            invoice_date: formatInvoiceLocalDate(invoiceDate),
            due_date: formatInvoiceLocalDate(dueDate),
            date_range: '',
            start_date: '',
            end_date: '',
            invoice_number: '',
            invoice_description: '',
            status: 'draft',
            invoice_file: null,
            invoice_file_id: null,
            invoice_file_name: '',
            linked_file: null,
            total: 0,
            total_paid: 0,
            balance: 0
        },
        selectedEntries: [],
        replaceSelectedEntries: false,
        selectedCaseExpenseIds: [],
        replaceSelectedCaseExpenses: false,
        replaceInvoiceFile: false,
        expectedSelectedEntries: [],
        expectedSelectedCaseExpenseIds: []
    };
};

const sortSerializableValue = (value) => {
    if (Array.isArray(value)) return value.map(sortSerializableValue);
    if (!value || typeof value !== 'object') return value;

    return Object.keys(value).sort().reduce((result, key) => {
        result[key] = sortSerializableValue(value[key]);
        return result;
    }, {});
};

export const serializeInvoiceDraft = (invoiceData = {}) => {
    const { revision: ignoredRevision, ...editableInvoice } = invoiceData?.invoice || {};
    return JSON.stringify(sortSerializableValue({
        invoice: editableInvoice,
        selectedEntries: Array.from(new Set(
            (Array.isArray(invoiceData?.selectedEntries) ? invoiceData.selectedEntries : [])
                .map(Number)
                .filter(entryId => Number.isInteger(entryId) && entryId > 0)
        )).sort((a, b) => a - b),
        selectedCaseExpenseIds: Array.from(new Set(
            (Array.isArray(invoiceData?.selectedCaseExpenseIds) ? invoiceData.selectedCaseExpenseIds : [])
                .map(Number)
                .filter(sourceId => Number.isInteger(sourceId) && sourceId > 0)
        )).sort((a, b) => a - b)
    }));
};

export const createFreshPaymentData = (caseId, now = new Date()) => ({
    case_id: Number(caseId),
    reference: '',
    date: formatInvoiceLocalDate(now),
    total: '',
    method: '',
    allocations: [],
    allocation_count: 0,
    allocated_total: 0,
    invoice_id: null,
    allocation_amount: null,
    _allocationMode: 'preserve',
});

export const hydratePaymentDraft = (paymentData, caseId) => {
    const allocations = Array.isArray(paymentData?.allocations)
        ? paymentData.allocations.map(allocation => ({ ...allocation }))
        : [];

    return {
        id: Number(paymentData?.id),
        revision: paymentData?.revision,
        void: Number(paymentData?.void || 0),
        total: paymentData?.total ?? '',
        date: String(paymentData?.date || '').split(' ')[0],
        reference: paymentData?.reference ?? '',
        method: paymentData?.method ?? '',
        status: paymentData?.status ?? null,
        case_id: Number(paymentData?.case_id || caseId),
        allocations,
        allocation_count: allocations.length,
        allocated_total: paymentData?.allocated_total
            ?? allocations.reduce((sum, allocation) => sum + Number(allocation?.allocation_amount || 0), 0),
        invoice_id: allocations.length === 1 ? Number(allocations[0]?.invoice_id) : null,
        allocation_amount: allocations.length === 1 ? allocations[0]?.allocation_amount : null,
        _allocationMode: 'preserve',
    };
};

const isValidPaymentDate = (value) => {
    const match = String(value || '').match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (!match) return false;
    const [, yearValue, monthValue, dayValue] = match;
    const year = Number(yearValue);
    const month = Number(monthValue);
    const day = Number(dayValue);
    if (year < 1970 || year > 2038) return false;
    const date = new Date(year, month - 1, day);
    return date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day;
};

const isValidSignedPaymentTotal = (value) => /^-?\d+(?:\.\d{1,2})?$/.test(String(value ?? '').trim())
    && Number(value) !== 0
    && Math.abs(Number(value)) < 100000000;

const isValidPositiveAllocation = (value) => /^\d+(?:\.\d{1,2})?$/.test(String(value ?? '').trim())
    && Number(value) > 0
    && Number(value) < 100000000;

export const getPaymentValidationErrors = (paymentData) => {
    const errors = {};
    const total = Number(paymentData?.total);
    const reference = String(paymentData?.reference || '').trim();
    const method = String(paymentData?.method || '').trim();
    const allocationMode = paymentData?._allocationMode || 'preserve';
    const allocations = Array.isArray(paymentData?.allocations) ? paymentData.allocations : [];
    const isVoidPayment = Number(paymentData?.void || 0) !== 0;

    if (!isValidPaymentDate(paymentData?.date)) {
        errors.date = 'Enter a valid payment date.';
    }
    if (!isValidSignedPaymentTotal(paymentData?.total)) {
        errors.total = 'Enter a nonzero payment or refund total with no more than two decimal places.';
    }
    if (!method) {
        errors.method = 'Enter a payment method.';
    } else if (method.length > 100) {
        errors.method = 'Payment method must be 100 characters or fewer.';
    }
    if (reference.length > 15) {
        errors.reference = 'Reference must be 15 characters or fewer.';
    }

    if (isVoidPayment && (allocationMode === 'replace' || (allocationMode === 'preserve' && allocations.length > 0))) {
        errors.invoice_id = 'Void payments cannot be applied to invoices. Clear the invoice allocation before saving.';
    } else if (total < 0 && (allocationMode === 'replace' || (allocationMode === 'preserve' && allocations.length > 0))) {
        errors.total = 'Refunds cannot be applied to invoices. Clear the invoice allocation before saving.';
    } else if (allocationMode === 'replace') {
        if (!Number.isInteger(Number(paymentData?.invoice_id)) || Number(paymentData?.invoice_id) <= 0) {
            errors.invoice_id = 'Select an invoice.';
        }
        if (!isValidPositiveAllocation(paymentData?.allocation_amount)) {
            errors.allocation_amount = 'Enter an amount greater than zero with no more than two decimal places.';
        } else if (Number(paymentData.allocation_amount) > total) {
            errors.allocation_amount = 'The invoice amount cannot exceed the payment total.';
        }
    } else if (allocationMode === 'preserve' && allocations.length > 0) {
        const allocatedTotal = allocations.reduce(
            (sum, allocation) => sum + Number(allocation?.allocation_amount || 0),
            0
        );
        if (Number.isFinite(total) && allocatedTotal > total) {
            errors.total = 'The payment total cannot be less than its existing invoice allocations.';
        }
    }

    return errors;
};

export const buildPaymentRequest = (paymentData, caseId) => {
    const payload = {
        case_id: Number(caseId),
        reference: String(paymentData?.reference || '').trim(),
        date: paymentData?.date,
        total: paymentData?.total,
        method: String(paymentData?.method || '').trim(),
    };
    if (Number(paymentData?.id) > 0) {
        payload.id = Number(paymentData.id);
        if (paymentData?.revision !== null && paymentData?.revision !== undefined && paymentData.revision !== '') {
            payload.expected_revision = paymentData.revision;
        }
    }

    if (paymentData?._allocationMode === 'replace') {
        payload.invoice_id = Number(paymentData.invoice_id);
        payload.allocation_amount = paymentData.allocation_amount;
    } else if (paymentData?._allocationMode === 'clear') {
        payload.invoice_id = null;
        payload.allocation_amount = null;
    }

    return payload;
};

export const buildPaymentDeleteRequest = (paymentData) => {
    const payload = { id: Number(paymentData?.id) };
    if (paymentData?.revision !== null && paymentData?.revision !== undefined && paymentData.revision !== '') {
        payload.expected_revision = paymentData.revision;
    }
    return payload;
};

export const isCurrentBillingCaseRequest = (
    activeCaseId,
    activeGeneration,
    requestCaseId,
    requestGeneration
) => String(activeCaseId) === String(requestCaseId)
    && Number(activeGeneration) === Number(requestGeneration);

export const getPaymentAllocationEditorInvoices = (available, invoices) => (
    available === true && Array.isArray(invoices) ? invoices : null
);

export const getPaymentAllocationSummary = (paymentData) => {
    const allocations = Array.isArray(paymentData?.allocations) ? paymentData.allocations : [];
    if (allocations.length === 0) return 'Unallocated';
    if (allocations.length === 1) {
        const allocation = allocations[0];
        return `#${allocation?.invoice_number || allocation?.invoice_id} · ${formatValue(allocation?.allocation_amount || 0, 'currency')}`;
    }
    return `${allocations.length} invoices · ${formatValue(paymentData?.allocated_total || 0, 'currency')}`;
};

export const paymentIsVoid = (paymentData) => {
    const status = String(paymentData?.status || '').trim().toLowerCase();
    return Number(paymentData?.void || 0) !== 0 || status === 'void' || status === 'voided';
};

export const getActivePaymentTotal = (payments = []) => (
    (Array.isArray(payments) ? payments : []).reduce((total, paymentData) => {
        if (paymentIsVoid(paymentData)) return total;
        const paymentTotal = Number(paymentData?.total || 0);
        return Number.isFinite(paymentTotal) ? total + paymentTotal : total;
    }, 0)
);

const invoiceIsInactive = (invoice) => {
    const status = String(invoice?.status || '').trim().toLowerCase();
    return ['void', 'voided', 'cancelled', 'canceled'].includes(status);
};

const getUnbilledEntryKind = (entry) => {
    const kind = String(entry?.entry_kind || entry?.item_type || 'time').trim().toLowerCase();
    if (kind === 'flatfee' || kind === 'flat') return 'flat_fee';
    return kind;
};

export const deriveBillingSummary = ({
    invoices = [],
    payments = [],
    unbilledEntries = [],
    rateEntries = [],
} = {}) => {
    const activeInvoices = (Array.isArray(invoices) ? invoices : []).filter(invoice => !invoiceIsInactive(invoice));
    const summary = {
        total_invoices: activeInvoices.reduce((total, invoice) => {
            const invoiceTotal = Number(invoice?.total || 0);
            return Number.isFinite(invoiceTotal) ? total + invoiceTotal : total;
        }, 0),
        total_transactions: getActivePaymentTotal(payments),
        balance: activeInvoices.reduce((total, invoice) => {
            const storedBalance = invoice?.balance;
            const balance = storedBalance !== null && storedBalance !== undefined && storedBalance !== ''
                ? Number(storedBalance)
                : Math.max(0, Number(invoice?.total || 0) - Number(invoice?.total_paid || 0));
            return total + (Number.isFinite(balance) ? balance : 0);
        }, 0),
        time_entries: 0,
        expenses: 0,
        flat_fee: 0,
    };

    const billingRateUserById = new Map(
        (Array.isArray(rateEntries) ? rateEntries : [])
            .map(rate => [Number(rate?.id), Number(rate?.user_id)])
            .filter(([rateId, userId]) => (
                Number.isInteger(rateId) && rateId > 0 && Number.isInteger(userId) && userId > 0
            ))
    );
    (Array.isArray(unbilledEntries) ? unbilledEntries : []).forEach(entry => {
        if (
            !billingEntryIsEligibleForInvoiceSelection(entry, billingRateUserById)
            || billingEntryHasInvoiceLink(entry)
        ) return;
        const kind = getUnbilledEntryKind(entry);
        const amount = kind === 'time'
            ? getBillingTimeEntryAuthoritativeTotal(entry) ?? 0
            : getBillingEntryTotal(entry);
        if (kind === 'expense') summary.expenses += amount;
        else if (kind === 'flat_fee') summary.flat_fee += amount;
        else if (kind === 'time') summary.time_entries += amount;
    });

    return summary;
};

export const TimeBilling = ({ case_id, caseData, guidedBillingDraft = null, onGuidedBillingDraftConsumed = () => {} }) => {
    const [billingNav, setBillingNav] = useState(0);
    const [screenWidth, setScreenWidth] = useState(window.innerWidth);
    const [billingEntries, setBillingEntries] = useState([]);
    const [unbilledBillingEntries, setUnbilledBillingEntries] = useState([]);
    const [invoices, setInvoices] = useState([]);
    const [createInvoice, setCreateInvoice] = useState(false);
    const [createPayment, setCreatePayment] = useState(false);
    const [payment, setPayment] = useState(null);
    const [newSettings, setNewSettings] = useState({});
    const [settings, setSettings] = useState({});
    const [rateSchedule, setRateSchedule] = useState(0);
    const [rateEntries, setRateEntries] = useState([]);
    const [invoiceFilePreview, setInvoiceFilePreview] = useState(null);
    const [loadingInvoiceFile, setLoadingInvoiceFile] = useState(false);
    const [invoiceData, setInvoiceData] = useState(() => createFreshInvoiceData());
    const [isSavingInvoice, setIsSavingInvoice] = useState(false);
    const [loadingInvoiceId, setLoadingInvoiceId] = useState(null);
    const [invoiceNotice, setInvoiceNotice] = useState(null);
    const [paymentErrors, setPaymentErrors] = useState({});
    const [paymentError, setPaymentError] = useState('');
    const [isSavingPayment, setIsSavingPayment] = useState(false);
    const [isDeletingPayment, setIsDeletingPayment] = useState(false);
    const [paymentAllocationsAvailable, setPaymentAllocationsAvailable] = useState(false);
    const invoiceSaveInFlightRef = useRef(false);
    const invoiceBaselineRef = useRef('');
    const invoiceEditorSessionRef = useRef(0);
    const paymentActionInFlightRef = useRef(false);
    const caseRequestGenerationRef = useRef(0);
    const activeCaseIdRef = useRef(String(case_id));
    const billingData = useMemo(() => deriveBillingSummary({
        invoices,
        payments: billingEntries?.payments,
        unbilledEntries: unbilledBillingEntries,
        rateEntries,
    }), [billingEntries?.payments, invoices, rateEntries, unbilledBillingEntries]);

    const isActiveCaseRequest = useCallback((requestCaseId, requestGeneration) => (
        isCurrentBillingCaseRequest(
            activeCaseIdRef.current,
            caseRequestGenerationRef.current,
            requestCaseId,
            requestGeneration
        )
    ), []);

    const beginInvoiceEditorSession = () => {
        invoiceEditorSessionRef.current += 1;
        return invoiceEditorSessionRef.current;
    };

    const invalidateInvoiceEditorSession = () => {
        invoiceEditorSessionRef.current += 1;
    };

    const setInvoiceDataForSession = useCallback((sessionKey, nextValue) => {
        if (invoiceEditorSessionRef.current !== sessionKey) return false;
        setInvoiceData(previousValue => typeof nextValue === 'function'
            ? nextValue(previousValue)
            : nextValue);
        return true;
    }, []);

    const fetchBillingSettings = useCallback(async ({
        requestCaseId = case_id,
        requestGeneration = caseRequestGenerationRef.current,
        signal,
    } = {}) => {
        try {
            const response = await fetch(`https://api.casedb.co/billing.php?settings=${requestCaseId}`, { signal });
            const data = await response.json().catch(() => ({}));
            if (!response.ok || !data.success) {
                throw new Error(data.message || `Billing settings could not be loaded (${response.status}).`);
            }
            if (signal?.aborted || !isActiveCaseRequest(requestCaseId, requestGeneration)) return null;

            setRateSchedule(data?.settings?.billing_rates_id);
            setNewSettings(data.settings || {});
            setSettings(data.settings || {});
            setRateEntries(data.billing_rate_entries || []);
            return data;
        } catch (error) {
            if (error.name !== 'AbortError' && isActiveCaseRequest(requestCaseId, requestGeneration)) {
                console.error('Error fetching billing settings:', error);
            }
            return null;
        }
    }, [case_id, isActiveCaseRequest]);

    const fetchBillingData = useCallback(async ({
        requestCaseId = case_id,
        requestGeneration = caseRequestGenerationRef.current,
        signal,
    } = {}) => {
        const readJson = async (url, label) => {
            const response = await fetch(url, { credentials: 'include', signal });
            const data = await response.json().catch(() => ({}));
            if (!response.ok || !data.success) {
                throw new Error(data.message || `${label} could not be loaded (${response.status}).`);
            }
            return data;
        };

        const [paymentsResult, unbilledResult] = await Promise.allSettled([
            readJson(`https://api.casedb.co/payments.php?case_id=${requestCaseId}`, 'Payments'),
            readJson(`https://api.casedb.co/billing_entries.php?case_id=${requestCaseId}&status=pending`, 'Unbilled entries'),
        ]);
        if (signal?.aborted || !isActiveCaseRequest(requestCaseId, requestGeneration)) return null;

        if (paymentsResult.status === 'fulfilled') {
            setBillingEntries(paymentsResult.value);
            setPaymentAllocationsAvailable(paymentsResult.value.payment_allocations_available === true);
        } else if (paymentsResult.reason?.name !== 'AbortError') {
            setPaymentAllocationsAvailable(false);
            console.error('Error fetching payments:', paymentsResult.reason);
        }

        if (unbilledResult.status === 'fulfilled') {
            setUnbilledBillingEntries(unbilledResult.value.billing_entries || []);
        } else if (unbilledResult.reason?.name !== 'AbortError') {
            console.error('Error fetching unbilled billing entries:', unbilledResult.reason);
        }

        return {
            payments: paymentsResult.status === 'fulfilled' ? paymentsResult.value : null,
            unbilledEntries: unbilledResult.status === 'fulfilled'
                ? unbilledResult.value.billing_entries || []
                : null,
        };
    }, [case_id, isActiveCaseRequest]);

    const fetchInvoices = useCallback(async ({
        requestCaseId = case_id,
        requestGeneration = caseRequestGenerationRef.current,
        signal,
    } = {}) => {
        try {
            const response = await fetch(`https://api.casedb.co/billing_entries.php?case_id=${requestCaseId}&invoices=true`, {
                credentials: 'include',
                signal,
            });
            const data = await response.json().catch(() => ({}));
            if (!response.ok || !data.success) {
                throw new Error(data.message || `Invoices could not be loaded (${response.status}).`);
            }
            if (signal?.aborted || !isActiveCaseRequest(requestCaseId, requestGeneration)) return null;

            setInvoices(data.invoices || []);
            return data;
        } catch (error) {
            if (error.name !== 'AbortError' && isActiveCaseRequest(requestCaseId, requestGeneration)) {
                console.error('Error fetching invoices:', error);
            }
            return null;
        }
    }, [case_id, isActiveCaseRequest]);

    const handleBillingEntriesChanged = useCallback(({ linkedInvoice = null } = {}) => {
        const requestCaseId = case_id;
        const requestGeneration = caseRequestGenerationRef.current;
        if (!isActiveCaseRequest(requestCaseId, requestGeneration)) return;

        if (linkedInvoice?.id) {
            setInvoices(current => current.map(invoice => (
                Number(invoice?.id) === Number(linkedInvoice.id)
                    ? { ...invoice, ...linkedInvoice }
                    : invoice
            )));
        }

        void fetchBillingData({ requestCaseId, requestGeneration });
    }, [case_id, fetchBillingData, isActiveCaseRequest]);

    const closeInvoiceEditor = () => {
        if (invoiceSaveInFlightRef.current) return false;

        const isDirty = invoiceBaselineRef.current
            && serializeInvoiceDraft(invoiceData) !== invoiceBaselineRef.current;
        if (isDirty && !window.confirm('Discard your unsaved invoice changes?')) {
            return false;
        }

        invalidateInvoiceEditorSession();
        setCreateInvoice(false);
        setInvoiceData(createFreshInvoiceData());
        invoiceBaselineRef.current = '';
        return true;
    };

    const openNewInvoice = () => {
        if (loadingInvoiceId || invoiceSaveInFlightRef.current) return;
        const freshInvoiceData = createFreshInvoiceData();
        beginInvoiceEditorSession();
        setInvoiceNotice(null);
        setInvoiceData(freshInvoiceData);
        invoiceBaselineRef.current = serializeInvoiceDraft(freshInvoiceData);
        setCreateInvoice(true);
    };

    const markInvoiceDataPersisted = (nextValue, sessionKey = invoiceEditorSessionRef.current) => {
        if (invoiceEditorSessionRef.current !== sessionKey) return false;
        setInvoiceData(previousValue => {
            const resolvedValue = typeof nextValue === 'function'
                ? nextValue(previousValue)
                : nextValue;
            invoiceBaselineRef.current = serializeInvoiceDraft(resolvedValue);
            return resolvedValue;
        });
        return true;
    };

    const postInvoice = async (options = {}) => {
        const keepOpen = Boolean(options?.keepOpen);
        const submittedInvoiceData = options?.invoiceData || invoiceData;
        if (invoiceSaveInFlightRef.current) {
            return { success: false, message: 'The invoice is already being saved.', in_flight: true };
        }

        const requestCaseId = case_id;
        const requestGeneration = caseRequestGenerationRef.current;
        const saveToken = Symbol('invoice-save');
        invoiceSaveInFlightRef.current = saveToken;
        setIsSavingInvoice(true);
        try {
            const isExistingInvoice = Boolean(submittedInvoiceData?.invoice?.id);
            const hasSelectedEntries = Array.isArray(submittedInvoiceData?.selectedEntries) && submittedInvoiceData.selectedEntries.length > 0;
            const hasSelectedCaseExpenses = Array.isArray(submittedInvoiceData?.selectedCaseExpenseIds)
                && submittedInvoiceData.selectedCaseExpenseIds.length > 0;

            if (!isExistingInvoice && !hasSelectedEntries && !hasSelectedCaseExpenses) {
                return { success: false, message: 'Select at least one billing entry or case expense.' };
            }
            
            // Send invoice data to backend
            const response = await fetch(`https://api.casedb.co/billing_entries.php?case_id=${case_id}&create_invoice=1`, {
                method: 'POST', // Use POST method for invoice creation
                headers: {
                    'Content-Type': 'application/json',
                },
                credentials: 'include',
                body: JSON.stringify({
                    invoice: {
                        ...submittedInvoiceData.invoice,
                        start_date: submittedInvoiceData?.invoice?.start_date || null,
                        end_date: submittedInvoiceData?.invoice?.end_date || null,
                        case_id: case_id
                    },
                    selectedEntries: submittedInvoiceData.selectedEntries,
                    replaceSelectedEntries: submittedInvoiceData?.replaceSelectedEntries === true,
                    selectedCaseExpenseIds: submittedInvoiceData.selectedCaseExpenseIds,
                    replaceSelectedCaseExpenses: submittedInvoiceData?.replaceSelectedCaseExpenses === true,
                    replaceInvoiceFile: submittedInvoiceData?.replaceInvoiceFile === true,
                    expected_revision: submittedInvoiceData?.invoice?.revision,
                    expectedSelectedEntries: Array.isArray(submittedInvoiceData?.expectedSelectedEntries)
                        ? submittedInvoiceData.expectedSelectedEntries
                        : [],
                    expectedSelectedCaseExpenseIds: Array.isArray(submittedInvoiceData?.expectedSelectedCaseExpenseIds)
                        ? submittedInvoiceData.expectedSelectedCaseExpenseIds
                        : []
                }),
            });

            const data = await response.json().catch(() => ({}));
            if (!response.ok || !data.success) {
                const message = data.message || `The invoice could not be saved (${response.status}).`;
                console.error('Failed to save invoice:', message);
                return { ...data, success: false, message };
            }
            
            const persistedInvoiceData = {
                ...submittedInvoiceData,
                invoice: {
                    ...submittedInvoiceData.invoice,
                    ...(data.invoice || {}),
                    id: Number(data.invoice_id || data.invoice?.id || submittedInvoiceData?.invoice?.id),
                    revision: data.invoice?.revision ?? submittedInvoiceData?.invoice?.revision
                },
                replaceSelectedEntries: false,
                replaceSelectedCaseExpenses: false,
                replaceInvoiceFile: false,
                expectedSelectedEntries: Array.from(new Set(
                    (Array.isArray(submittedInvoiceData?.selectedEntries) ? submittedInvoiceData.selectedEntries : [])
                        .map(Number)
                        .filter(entryId => Number.isInteger(entryId) && entryId > 0)
                )).sort((a, b) => a - b),
                expectedSelectedCaseExpenseIds: Array.from(new Set(
                    (Array.isArray(submittedInvoiceData?.selectedCaseExpenseIds) ? submittedInvoiceData.selectedCaseExpenseIds : [])
                        .map(Number)
                        .filter(sourceId => Number.isInteger(sourceId) && sourceId > 0)
                )).sort((a, b) => a - b)
            };

            if (isActiveCaseRequest(requestCaseId, requestGeneration)) {
                if (keepOpen) {
                    markInvoiceDataPersisted(persistedInvoiceData);
                } else {
                    invalidateInvoiceEditorSession();
                    setCreateInvoice(false);
                    setInvoiceData(createFreshInvoiceData());
                    invoiceBaselineRef.current = '';
                }

                void fetchInvoices({ requestCaseId, requestGeneration });
                void fetchBillingData({ requestCaseId, requestGeneration });
            }

            return { ...data, success: true, invoice: persistedInvoiceData.invoice };
        } catch (error) {
            console.error('Error saving invoice:', error);
            return { success: false, message: error.message || 'The invoice could not be saved.' };
        } finally {
            if (invoiceSaveInFlightRef.current === saveToken) {
                invoiceSaveInFlightRef.current = false;
                if (isActiveCaseRequest(requestCaseId, requestGeneration)) {
                    setIsSavingInvoice(false);
                }
            }
        }
    };

    const closePaymentEditor = () => {
        if (paymentActionInFlightRef.current) return;
        setCreatePayment(false);
        setPayment(null);
        setPaymentErrors({});
        setPaymentError('');
    };

    const openNewPayment = () => {
        if (paymentActionInFlightRef.current) return;
        setPayment(createFreshPaymentData(case_id));
        setPaymentErrors({});
        setPaymentError('');
        setCreatePayment(true);
    };

    const updatePaymentDraft = (nextValue) => {
        setPayment(previousValue => typeof nextValue === 'function'
            ? nextValue(previousValue)
            : nextValue);
        if (Object.keys(paymentErrors).length > 0) setPaymentErrors({});
        if (paymentError) setPaymentError('');
    };

    const postPayment = async () => {
        if (paymentActionInFlightRef.current) return;

        const validationErrors = getPaymentValidationErrors(payment);
        if (Object.keys(validationErrors).length > 0) {
            setPaymentErrors(validationErrors);
            setPaymentError('Correct the highlighted payment details and try again.');
            return;
        }

        const isUpdate = Number(payment?.id) > 0;
        const requestCaseId = case_id;
        const requestGeneration = caseRequestGenerationRef.current;
        const saveToken = Symbol('payment-save');
        paymentActionInFlightRef.current = saveToken;
        setIsSavingPayment(true);
        setPaymentErrors({});
        setPaymentError('');
        try {
            const response = await fetch('https://api.casedb.co/payments.php', {
                method: isUpdate ? 'PUT' : 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                credentials: 'include',
                body: JSON.stringify(buildPaymentRequest(payment, case_id)),
            });
            const data = await response.json().catch(() => ({}));
            if (!response.ok || !data.success) {
                const message = data.message || `The payment could not be ${isUpdate ? 'updated' : 'saved'} (${response.status}).`;
                if (isActiveCaseRequest(requestCaseId, requestGeneration)) setPaymentError(message);
                return;
            }

            if (isActiveCaseRequest(requestCaseId, requestGeneration)) {
                setCreatePayment(false);
                setPayment(null);
                await Promise.all([
                    fetchBillingData({ requestCaseId, requestGeneration }),
                    fetchInvoices({ requestCaseId, requestGeneration }),
                ]);
            }
        } catch (error) {
            console.error('Error saving payment:', error);
            if (isActiveCaseRequest(requestCaseId, requestGeneration)) {
                setPaymentError(error.message || 'The payment could not be saved. Please try again.');
            }
        } finally {
            if (paymentActionInFlightRef.current === saveToken) {
                paymentActionInFlightRef.current = false;
                if (isActiveCaseRequest(requestCaseId, requestGeneration)) setIsSavingPayment(false);
            }
        }
    };

    const deletePayment = async () => {
        if (!payment?.id || paymentActionInFlightRef.current) return;

        const requestCaseId = case_id;
        const requestGeneration = caseRequestGenerationRef.current;
        const deleteToken = Symbol('payment-delete');
        paymentActionInFlightRef.current = deleteToken;
        setIsDeletingPayment(true);
        setPaymentError('');
        try {
            const response = await fetch('https://api.casedb.co/payments.php', {
                method: 'DELETE',
                headers: {
                    'Content-Type': 'application/json',
                },
                credentials: 'include',
                body: JSON.stringify(buildPaymentDeleteRequest(payment)),
            });
            const data = await response.json().catch(() => ({}));
            if (!response.ok || !data.success) {
                if (isActiveCaseRequest(requestCaseId, requestGeneration)) {
                    setPaymentError(data.message || `The payment could not be deleted (${response.status}).`);
                }
                return;
            }

            if (isActiveCaseRequest(requestCaseId, requestGeneration)) {
                setCreatePayment(false);
                setPayment(null);
                await Promise.all([
                    fetchBillingData({ requestCaseId, requestGeneration }),
                    fetchInvoices({ requestCaseId, requestGeneration }),
                ]);
            }
        } catch (error) {
            console.error('Error deleting payment:', error);
            if (isActiveCaseRequest(requestCaseId, requestGeneration)) {
                setPaymentError(error.message || 'The payment could not be deleted. Please try again.');
            }
        } finally {
            if (paymentActionInFlightRef.current === deleteToken) {
                paymentActionInFlightRef.current = false;
                if (isActiveCaseRequest(requestCaseId, requestGeneration)) setIsDeletingPayment(false);
            }
        }
    };

    const handlePaymentClick = (paymentData) => {
        if (paymentActionInFlightRef.current) return;
        setPayment(hydratePaymentDraft(paymentData, case_id));
        setPaymentErrors({});
        setPaymentError('');
        setCreatePayment(true);
    };

    const handleInvoiceClick = async (invoice) => {
        if (!invoice?.id || loadingInvoiceId || invoiceSaveInFlightRef.current) return;

        const requestCaseId = case_id;
        const requestGeneration = caseRequestGenerationRef.current;
        setLoadingInvoiceId(invoice.id);
        setInvoiceNotice(null);
        try {
            const url = `https://api.casedb.co/billing_entries.php?invoice_id=${invoice.id}`;
            const response = await fetch(url, { credentials: 'include' });
            const data = await response.json().catch(() => ({}));
            
            if (!isActiveCaseRequest(requestCaseId, requestGeneration)) return;

            if (response.ok && data.success && data.invoice) {
                const invoiceInfo = data.invoice;
                const linkedEntryIds = data.linked_billing_entries || [];
                const linkedCaseExpenseIds = data.linked_case_expense_ids || [];
                
                const loadedInvoiceData = {
                    invoice: {
                        invoice_date: invoiceInfo.invoice_date?.split(' ')[0] || invoiceInfo.invoice_date,
                        due_date: invoiceInfo.due_date?.split(' ')[0] || invoiceInfo.due_date,
                        date_range: `${invoiceInfo.start_date || ''} - ${invoiceInfo.end_date || ''}`,
                        start_date: invoiceInfo.start_date?.split(' ')[0] || invoiceInfo.start_date || '',
                        end_date: invoiceInfo.end_date?.split(' ')[0] || invoiceInfo.end_date || '',
                        invoice_number: invoiceInfo.invoice_number,
                        invoice_description: invoiceInfo.invoice_description || '',
                        status: invoiceInfo.status || 'draft',
                        invoice_file: invoiceInfo.invoice_file || null,
                        invoice_file_id: invoiceInfo.invoice_file_id || invoiceInfo.linked_file?.id || null,
                        invoice_file_name: invoiceInfo.invoice_file_name || invoiceInfo.linked_file?.file_name || '',
                        invoice_file_path: invoiceInfo.invoice_file_path || invoiceInfo.linked_file?.file_path || null,
                        invoice_file_url: invoiceInfo.invoice_file_url || invoiceInfo.linked_file?.file_url || null,
                        linked_file: invoiceInfo.linked_file || null,
                        total: invoiceInfo.total,
                        total_paid: invoiceInfo.total_paid,
                        balance: invoiceInfo.balance,
                        id: invoiceInfo.id,
                        revision: invoiceInfo.revision
                    },
                    selectedEntries: linkedEntryIds.map(Number).filter(entryId => Number.isInteger(entryId) && entryId > 0),
                    replaceSelectedEntries: false,
                    selectedCaseExpenseIds: linkedCaseExpenseIds.map(Number).filter(sourceId => Number.isInteger(sourceId) && sourceId > 0),
                    replaceSelectedCaseExpenses: false,
                    replaceInvoiceFile: false,
                    expectedSelectedEntries: linkedEntryIds.map(Number).filter(entryId => Number.isInteger(entryId) && entryId > 0),
                    expectedSelectedCaseExpenseIds: linkedCaseExpenseIds.map(Number).filter(sourceId => Number.isInteger(sourceId) && sourceId > 0),
                    linkedCaseExpenses: Array.isArray(data.linked_case_expenses) ? data.linked_case_expenses : []
                };
                beginInvoiceEditorSession();
                setInvoiceData(loadedInvoiceData);
                invoiceBaselineRef.current = serializeInvoiceDraft(loadedInvoiceData);
                
                setCreateInvoice(true);
            } else {
                const message = data.message || 'The invoice could not be loaded.';
                console.error('Failed to fetch invoice data:', message);
                setInvoiceNotice({ type: 'error', message });
            }
        } catch (error) {
            if (isActiveCaseRequest(requestCaseId, requestGeneration)) {
                console.error('Error fetching invoice data:', error);
                setInvoiceNotice({ type: 'error', message: error.message || 'The invoice could not be loaded.' });
            }
        } finally {
            if (isActiveCaseRequest(requestCaseId, requestGeneration)) setLoadingInvoiceId(null);
        }
    };

    const handleInvoiceRowKeyDown = (event, invoice) => {
        if (event.repeat || (event.key !== 'Enter' && event.key !== ' ')) return;
        event.preventDefault();
        handleInvoiceClick(invoice);
    };

    useLayoutEffect(() => {
        const requestGeneration = caseRequestGenerationRef.current + 1;
        const requestCaseId = case_id;
        const controller = new AbortController();

        caseRequestGenerationRef.current = requestGeneration;
        activeCaseIdRef.current = String(requestCaseId);
        invalidateInvoiceEditorSession();

        setBillingNav(0);
        setBillingEntries([]);
        setUnbilledBillingEntries([]);
        setInvoices([]);
        setPaymentAllocationsAvailable(false);
        setNewSettings({});
        setSettings({});
        setRateSchedule(0);
        setRateEntries([]);
        setCreateInvoice(false);
        setInvoiceData(createFreshInvoiceData());
        setLoadingInvoiceId(null);
        setInvoiceNotice(null);
        setInvoiceFilePreview(null);
        setLoadingInvoiceFile(false);
        setCreatePayment(false);
        setPayment(null);
        setPaymentErrors({});
        setPaymentError('');
        setIsSavingInvoice(false);
        setIsSavingPayment(false);
        setIsDeletingPayment(false);
        invoiceBaselineRef.current = '';
        invoiceSaveInFlightRef.current = false;
        paymentActionInFlightRef.current = false;

        const requestOptions = {
            requestCaseId,
            requestGeneration,
            signal: controller.signal,
        };
        void Promise.all([
            fetchBillingData(requestOptions),
            fetchBillingSettings(requestOptions),
            fetchInvoices(requestOptions),
        ]);

        return () => controller.abort();
    }, [case_id, fetchBillingData, fetchBillingSettings, fetchInvoices]);

    useEffect(() => {
        const handleResize = () => setScreenWidth(window.innerWidth);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const renderedInvoiceEditorSession = invoiceEditorSessionRef.current;

    return (
        <div className='case-section'>
            <div id='template-header' className='alt-header'>
                {screenWidth < 729 ? (
                    <select className='default-select' value={billingNav} onChange={(e) => setBillingNav(Number(e.target.value))}>
                        <option value={0}>Summary</option>
                        <option value={1}>Billing Entries</option>
                        <option value={2}>Invoices</option>
                        <option value={3}>Project Funds</option>
                        <option value={4}>Settings</option>
                    </select>
                )  : (
                    <>
                        <h4 onClick={() => setBillingNav(0)} className={`${billingNav !== 0 ? 'in' : ''}active`}>Summary</h4>
                        <h4 onClick={() => setBillingNav(1)} className={`${billingNav !== 1 ? 'in' : ''}active`}>Billing Entries</h4>
                        <h4 onClick={() => setBillingNav(2)} className={`${billingNav !== 2 ? 'in' : ''}active`}>Invoices</h4>
                        <h4 onClick={() => setBillingNav(3)} className={`${billingNav !== 3 ? 'in' : ''}active`}>Project Funds</h4>
                        <h4 onClick={() => setBillingNav(4)} className={`${billingNav !== 4 ? 'in' : ''}active`}>Settings</h4>
                    </>
                )}
            </div>
            {billingNav === 0 ? (
                <div className='billing-summary'>
                    <div className='billing-summary-details'>
                        <div className='billing-summary-totals'>
                            <SubtextTitle title='Summary' />
                            <div className='billing-invoice-totals'>
                                <span className='subtext'>
                                    Total Invoices
                                </span>
                                <span className='subtext'>
                                    {formatValue(billingData.total_invoices ? billingData.total_invoices : 0, 'currency')}
                                </span>
                            </div>
                            <div className='billing-invoice-totals'>
                                <span className='subtext'>
                                    Total Transactions
                                </span>
                                <span className='subtext'>
                                    {formatValue(billingData.total_transactions, 'currency')}
                                </span>
                            </div>
                            <div className='billing-invoice-totals'>
                                <span className='subtext'>
                                    Current Balance
                                </span>
                                <span className='subtext'>
                                    {formatValue(billingData.balance ? billingData.balance : 0, 'currency')}
                                </span>
                            </div>
                        </div>
                        <div className='billing-invoices'>
                            <Subheader title='Invoices' action={<button className='action tertiary' onClick={openNewInvoice} disabled={Boolean(loadingInvoiceId)}><Plus size={18} />New Invoice</button>} />
                            <div className='billing-table-scroll'>
                                <table className='billing-table table-full default billing-summary-invoice-table'>
                                    <thead>
                                        <tr className='subtext'>
                                            <th>Date</th>
                                            <th>Inv. #</th>
                                            <th>Description</th>
                                            <th>Total</th>
                                            <th>Outstanding</th>
                                            <th>Due Date</th>
                                            <th>Status</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {invoices?.length > 0 ? invoices.map((invoice) => (
                                            <tr 
                                                key={invoice.id}
                                                className='billing-invoice-row'
                                                onClick={() => handleInvoiceClick(invoice)}
                                                onKeyDown={(event) => handleInvoiceRowKeyDown(event, invoice)}
                                                tabIndex={0}
                                                style={{ 
                                                    opacity: loadingInvoiceFile || loadingInvoiceId === invoice.id ? 0.6 : 1
                                                }}
                                                aria-busy={loadingInvoiceId === invoice.id}
                                                aria-disabled={Boolean(loadingInvoiceId)}
                                                aria-label={`Edit invoice ${invoice.invoice_number || invoice.id}`}
                                                title='Click to edit invoice'
                                            >
                                                <td>{formatDate(invoice.invoice_date)[3]}</td>
                                                <td>{invoice.invoice_number}</td>
                                                <td>{invoice.invoice_description || invoice.description || ''}</td>
                                                <td>{formatValue(invoice.total, 'currency')}</td>
                                                <td>{formatValue(invoice.balance, 'currency')}</td>
                                                <td>{formatDate(invoice.due_date)[3]}</td>
                                                <td>{getInvoiceStatusLabel(invoice.status)}</td>
                                            </tr>
                                        )) : (
                                            <tr className='billing-empty-row'>
                                                <td colSpan={7}>No invoices found for this case.</td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                    <div className='billing-summary-details'>
                        <div className='billing-summary-totals'>
                            <SubtextTitle title='Unbilled' />
                            <div className='billing-invoice-totals'>
                                <span className='subtext'>
                                    Time Entries
                                </span>
                                <span className='subtext'>
                                    {formatValue(billingData.time_entries ? billingData.time_entries : 0, 'currency')}
                                </span>
                            </div>
                            <div className='billing-invoice-totals'>
                                <span className='subtext'>
                                    Expenses
                                </span>
                                <span className='subtext'>
                                {formatValue(billingData.expenses ? billingData.expenses : 0, 'currency')}
                                </span>
                            </div>
                            <div className='billing-invoice-totals'>
                                <span className='subtext'>
                                    Flat Fee
                                </span>
                                <span className='subtext'>
                                    {formatValue(billingData.flat_fee ? billingData.flat_fee : 0, 'currency')}
                                </span>
                            </div>
                        </div>
                        <div className='billing-invoices'>
                            <Subheader title='Transactions' action={<button className='action tertiary' onClick={openNewPayment} disabled={isSavingPayment || isDeletingPayment}><Plus size={18} />New Transaction</button>} />
                            <div className='billing-table-scroll'>
                                <table className='billing-table table-full default billing-transactions-table'>
                                    <thead>
                                        <tr className='subtext'>
                                            <th>Date</th>
                                            <th>Method</th>
                                            <th>Reference #</th>
                                            <th>Invoice</th>
                                            <th>Total</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {billingEntries?.payments?.length > 0 ? billingEntries?.payments?.map((payment) => (
                                            <tr 
                                                key={payment.id}
                                                className={paymentIsVoid(payment) ? 'billing-void-transaction' : ''}
                                                onClick={() => handlePaymentClick(payment)}
                                                style={{ 
                                                    cursor: 'pointer'
                                                }}
                                                title='Click to edit payment'
                                            >
                                                <td title={formatDate(payment.date)[3]}>{formatDate(payment.date)[3]}</td>
                                                <td title={payment.method || ''}>{String(payment.method || '').length > 20 ? `${String(payment.method).slice(0, 20)}...` : payment.method}</td>
                                                <td title={payment.reference || ''}>{payment.reference}</td>
                                                <td title={getPaymentAllocationSummary(payment)}>{getPaymentAllocationSummary(payment)}</td>
                                                <td title={paymentIsVoid(payment) ? `Void payment: ${payment.total}` : payment.total}>
                                                    <span>{formatValue(payment.total, 'currency')}</span>
                                                    {paymentIsVoid(payment) && <span className='tag caps payment-void-tag'>Void</span>}
                                                </td>
                                            </tr>
                                        )) : (
                                            <tr className='billing-empty-row'>
                                                <td colSpan={5} style={{ textAlign: 'center' }}>
                                                    No transactions found for this case.
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </div>
            ) : billingNav === 1 ? (
                    <BillingEntries
                        case_id={case_id}
                        rateEntries={rateEntries}
                        guidedBillingDraft={guidedBillingDraft}
                        onGuidedBillingDraftConsumed={onGuidedBillingDraftConsumed}
                        onEntriesChanged={handleBillingEntriesChanged}
                    />
            ) : billingNav === 2 ? (
                <div className='billing-invoices-section'>
                    <Subheader 
                        title='Invoices' 
                        action={
                            <button className='action tertiary' onClick={openNewInvoice} disabled={Boolean(loadingInvoiceId)}>
                                <Plus size={18} />New Invoice
                            </button>
                        } 
                    />
                    <div className='billing-table-scroll'>
                        <table className='billing-table table-full default billing-invoices-table'>
                            <thead>
                                <tr className='subtext'>
                                    <th>Invoice #</th>
                                    <th>Invoice Date</th>
                                    <th>Due Date</th>
                                    <th>Status</th>
                                    <th>Total</th>
                                    <th>Balance</th>
                                </tr>
                            </thead>
                            <tbody>
                                {invoices?.length > 0 ? invoices.map((invoice) => (
                                    <tr 
                                        key={invoice.id}
                                        className='billing-invoice-row'
                                        onClick={() => handleInvoiceClick(invoice)}
                                        onKeyDown={(event) => handleInvoiceRowKeyDown(event, invoice)}
                                        tabIndex={0}
                                        style={{ 
                                            opacity: loadingInvoiceFile || loadingInvoiceId === invoice.id ? 0.6 : 1
                                        }}
                                        aria-busy={loadingInvoiceId === invoice.id}
                                        aria-disabled={Boolean(loadingInvoiceId)}
                                        aria-label={`Edit invoice ${invoice.invoice_number || invoice.id}`}
                                        title='Click to edit invoice'
                                    >
                                        <td>{invoice.invoice_number}</td>
                                        <td>{formatDate(invoice.invoice_date)[3]}</td>
                                        <td>{formatDate(invoice.due_date)[3]}</td>
                                        <td>{getInvoiceStatusLabel(invoice.status)}</td>
                                        <td>{formatValue(invoice.total, 'currency')}</td>
                                        <td>{formatValue(invoice.balance, 'currency')}</td>
                                    </tr>
                                )) : (
                                    <tr className='billing-empty-row'>
                                        <td colSpan={6}>No invoices found for this case.</td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            ) : billingNav === 3 ? (
                <div>Project Funds Content</div>
            ) : billingNav === 4 ? (
                <BillingSettings case_id={case_id} settings={settings} newSettings={newSettings} setNewSettings={setNewSettings} rateSchedule={rateSchedule} />
            ) : (
                <></>
            )}
            {createInvoice && 
                <CreateInvoice 
                    caseData={caseData || { case: { id: case_id, case_name: 'Current Case' } }} 
                    timeEntries={billingEntries?.time_entries} 
                    rateEntries={rateEntries}
                    onClose={closeInvoiceEditor}
                    saveInvoice={postInvoice} 
                    invoiceData={invoiceData} 
                    setInvoiceData={(nextValue) => setInvoiceDataForSession(renderedInvoiceEditorSession, nextValue)}
                    isSavingInvoice={isSavingInvoice}
                    markInvoiceDataPersisted={(nextValue) => markInvoiceDataPersisted(nextValue, renderedInvoiceEditorSession)}
                />
            }
            {createPayment && 
                <Modal
                    onClose={closePaymentEditor}
                    title={payment?.id ? "Edit Payment" : "New Payment"}
                    single
                    footer={(
                        <div className='modal-footer-actions'>
                            <button
                                className='action alt'
                                onClick={closePaymentEditor}
                                disabled={isSavingPayment || isDeletingPayment}
                            >
                                Cancel
                            </button>
                            {payment?.id && (
                                <button
                                    className='action destroy'
                                    disabled={isSavingPayment || isDeletingPayment}
                                    onClick={() => {
                                        if (window.confirm('Are you sure you want to delete this payment?')) {
                                            deletePayment();
                                        }
                                    }}
                                >
                                    {isDeletingPayment ? 'Deleting…' : 'Delete'}
                                </button>
                            )}
                            <button
                                className='action'
                                onClick={postPayment}
                                disabled={isSavingPayment || isDeletingPayment}
                            >
                                {isSavingPayment ? 'Saving…' : payment?.id ? 'Update' : 'Save'}
                            </button>
                        </div>
                    )}
                >
                    <CreatePayment
                        payment={payment}
                        setPayment={updatePaymentDraft}
                        invoices={getPaymentAllocationEditorInvoices(paymentAllocationsAvailable, invoices)}
                        errors={paymentErrors}
                        apiError={paymentError}
                        disabled={isSavingPayment || isDeletingPayment}
                    />
                </Modal>
            }
            {invoiceFilePreview && 
                <Modal
                    onClose={() => setInvoiceFilePreview(null)}
                    title={`Invoice ${invoiceFilePreview.invoiceNumber} - ${invoiceFilePreview.fileName}`}
                    large
                    footer={(
                        <div className='modal-footer-actions'>
                            <button
                                className='action alt'
                                onClick={() => setInvoiceFilePreview(null)}
                            >
                                Close
                            </button>
                        </div>
                    )}
                >
                    <div className='modal-content-wrapper' style={{
                        width: '100%', 
                        height: '80vh', 
                        display: 'flex', 
                        justifyContent: 'center', 
                        alignItems: 'center' 
                    }}>
                        {invoiceFilePreview.fileType === 'pdf' ? (
                            <iframe
                                src={invoiceFilePreview.url}
                                style={{ 
                                    width: '100%', 
                                    height: '100%', 
                                    border: 'none',
                                    borderRadius: '6px'
                                }}
                                title={`Invoice ${invoiceFilePreview.invoiceNumber}`}
                            />
                        ) : ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'].includes(invoiceFilePreview.fileType) ? (
                            <img
                                src={invoiceFilePreview.url}
                                alt={`Invoice ${invoiceFilePreview.invoiceNumber}`}
                                style={{ 
                                    maxWidth: '100%', 
                                    maxHeight: '100%',
                                    objectFit: 'contain',
                                    borderRadius: '6px'
                                }}
                            />
                        ) : (
                            <div style={{ 
                                textAlign: 'center', 
                                padding: '40px',
                                color: 'var(--subtext-color)'
                            }}>
                                <p>Preview not available for this file type.</p>
                                <button
                                    className='action'
                                    onClick={() => window.open(invoiceFilePreview.url, '_blank')}
                                >
                                    Download File
                                </button>
                            </div>
                        )}
                    </div>
                </Modal>
            }
            {invoiceNotice && (
                <Alert
                    type={invoiceNotice.type}
                    message={invoiceNotice.message}
                    onClose={() => setInvoiceNotice(null)}
                    duration={6000}
                    dismissOnClick={false}
                    showClose
                />
            )}
        </div>
    );
}
